import { NotificationVariantModel } from '../../models/notification/notification-variant.model'
import { DomainDto } from '../../dtos/domain'
import { NotificationSource } from '../../enums/notification-source'
import { FEAT_AUTO_KW_DISCOVERY } from '../../constants'
import { getDefaultNotificationActions, getEnabledDeliveryChannels } from '../../_utils/domain'
import { NotificationDeliverySpec } from '@pushly/models/lib/structs/notification-delivery-spec'
import { NotificationDeliveryType } from '@pushly/models/lib/enums/notification-delivery-type'
import { NotificationDeliveryWindow } from '@pushly/models/lib/enums/notification-delivery-window'
import { CreateBadgeBehaviorRequest } from '@pushly/models/lib/structs/notifications/channels/create-badge-behavior-request'
import { IosBadgeBehaviorAction as ModelsIosBadgeBehaviorAction } from '@pushly/models/lib/enums/ios-badge-behavior'
import { safeEnumFromValue } from '@pushly/models'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { IosBadgeBehaviorAction, NotificationBuilderSubmitType } from '../../components/notification-builder/enums'
import { stripUndefined } from '../../_utils/strip-undefined'
import { NotificationAudienceModel } from '../../models/notification/notification-audience.model'
import { hasFlag } from '../../core/flags/has-flag'
import { BadgeBehaviorModel } from '../../models/notification/notification-native-ios-content.model'
import { CreateDomainNotificationRequest } from '@pushly/models/lib/structs/notifications/create-requests/create-domain-notification-request'
import { CreateNotificationTemplateRequest } from '@pushly/models/lib/structs/notifications/create-requests/create-notification-template-request'
import { CreateNotificationTemplateChannelsRequest } from '@pushly/models/lib/structs/notifications/create-requests/create-notification-template-channels-request'
import { CreateNotificationTemplateDefaultChannelRequest } from '@pushly/models/lib/structs/notifications/create-requests/create-notification-template-default-channel-request'
import { NotificationTemplateNativeIosChannelRequest } from '@pushly/models/lib/structs/notifications/channels/notification-template-native-ios-channel-request'
import { NotificationTemplateNativeAndroidChannel } from '@pushly/models/lib/structs/notifications/channels/notification-template-native-android-channel'
import { NotificationTemplateWebChannel } from '@pushly/models/lib/structs/notifications/channels/notification-template-web-channel'
import { PersonalPreviewAudience } from '@pushly/models/lib/structs/notifications/notification-audience/domain-audience/personal-preview-audience'
import { TeamPreviewAudience } from '@pushly/models/lib/structs/notifications/notification-audience/domain-audience/team-preview-audience'
import { SegmentsAudience } from '@pushly/models/lib/structs/notifications/notification-audience/domain-audience/segments-audience'
import { AllSubscribersAudience } from '@pushly/models/lib/structs/notifications/notification-audience/domain-audience/all-subscribers-audience'
import { AccountDomainsAudience } from '@pushly/models/lib/structs/notifications/notification-audience/account-audience/account-domains-audience'
import { NotificationAction } from '@pushly/models/lib/structs/notification-action'
import { ResourceContext } from './types/resource-context'
import type { IJourneyActionNotificationRequest } from '@pushly/models/lib/structs/journeys/requests/request-interfaces'
import { normalizeCollapseKey } from '@pushly/aqe/lib/utils/notification-collapse-key'

// region NotificationPayload helpers

export function buildNotificationAudiencePayload(
    audience: NotificationAudienceModel,
    submitType: NotificationBuilderSubmitType.TEAM_PREVIEW,
): TeamPreviewAudience
export function buildNotificationAudiencePayload(
    audience: NotificationAudienceModel,
    submitType: NotificationBuilderSubmitType.PERSONAL_PREVIEW,
): PersonalPreviewAudience
export function buildNotificationAudiencePayload(
    audience: NotificationAudienceModel,
    submitType: NotificationBuilderSubmitType.STANDARD,
    context: ResourceContext,
): SegmentsAudience | AllSubscribersAudience | AccountDomainsAudience
export function buildNotificationAudiencePayload(
    audience: NotificationAudienceModel,
    submitType: NotificationBuilderSubmitType,
    context?: ResourceContext,
): SegmentsAudience | AllSubscribersAudience | AccountDomainsAudience | PersonalPreviewAudience | TeamPreviewAudience {
    if (submitType === NotificationBuilderSubmitType.PERSONAL_PREVIEW) {
        return PersonalPreviewAudience.from({ personal_preview: true })
    } else if (submitType === NotificationBuilderSubmitType.TEAM_PREVIEW) {
        return TeamPreviewAudience.from({ team_preview: true })
    } else {
        if (!context) {
            throw new Error('Domain|Account context must be provided to build a notification audience payload')
        }

        if (context?.level === 'org') {
            return AccountDomainsAudience.from({
                domain_ids: audience.getIsAll()
                    ? context.account.domains.map((d) => d.id)
                    : audience.getDomainIds() ?? [],
                segment_ids: audience.getSegmentIds(),
            })
        } else {
            return SegmentsAudience.from({
                excluded_segment_ids: audience.getExcludedSegmentIds(),
                segment_ids: audience.getSegmentIds() ?? [],
            })
        }
    }
}

export const buildNotificationActionsPayload = (variant: NotificationVariantModel, domain: DomainDto) => {
    const defaultContent = variant.getContent().getDefaultContent()
    let actions: NotificationAction[] | undefined = defaultContent.getActions()?.map((action) =>
        NotificationAction.from({
            ordinal: action.getOrdinal() ?? 0,
            type: action.getType(),
            label: action.getLabel() ?? '',
            landing_url: action.getLandingUrl(),
            use_primary_landing_url: action.getUsePrimaryLandingUrl(),
        }),
    )

    if (defaultContent.getCustomActionsEnabled() && (actions?.length ?? 0) === 0) {
        actions = getDefaultNotificationActions(domain).map((action) =>
            NotificationAction.from({
                ordinal: action.ordinal,
                type: action.type,
                label: action.label,
                landing_url: action.landingUrl,
                use_primary_landing_url: action.usePrimaryLandingUrl,
            }),
        )
    }

    return actions
}

export const parseDomainNotificationBuildFromVariant = (
    variant: NotificationVariantModel,
    domain: DomainDto,
): CreateDomainNotificationRequest => {
    const audience = variant.getAudience()
    const content = variant.getContent()
    const defaultContent = content.getDefaultContent()
    const webContent = content.getWebContent()
    const nativeIosContent = content.getNativeIosContent()
    const nativeAndroidContent = content.getNativeAndroidContent()
    const exp = content.getDefaultContent().getExperienceOptions()

    const hasAutoKwDiscoveryFlag = hasFlag(domain, FEAT_AUTO_KW_DISCOVERY)

    const channels = variant.getChannels() ?? getEnabledDeliveryChannels(domain)
    const audiencePayload = buildNotificationAudiencePayload(audience, NotificationBuilderSubmitType.STANDARD, {
        level: 'domain',
        domain,
    })

    const ttl_seconds = exp.getTtlSeconds()
    const ttl_metric = exp.getTtlMetric()
    const deliverySpec = NotificationDeliverySpec.from({
        type: NotificationDeliveryType.IMMEDIATE,
        window: NotificationDeliveryWindow.STANDARD,
        ttl_seconds,
        ttl_metric,
    })

    let actions: NotificationAction[] | undefined = buildNotificationActionsPayload(variant, domain)

    // actions from UI are the same across applicable channels
    const templateActions = defaultContent.getCustomActionsEnabled() ? actions : undefined
    const actionsDisplayMeta = {
        override_default_actions: defaultContent.getCustomActionsEnabled(),
    }

    // build default channel
    const defaultChannel = CreateNotificationTemplateDefaultChannelRequest.from({
        title: defaultContent.getTitle()!,
        body: defaultContent.getBody() ?? undefined,
        landing_url:
            defaultContent.getLandingUrl() === '' || defaultContent.getLandingUrl() === undefined
                ? undefined
                : defaultContent.getLandingUrl()!,
        image_url: defaultContent.getImageUrl() ?? undefined,
        icon_url: defaultContent.getIconUrl() ?? undefined,
        content_webhook_url: defaultContent.getContentWebhookUrl(),
        override_fallback_template: defaultContent.getOverrideFallbackTemplate(),
        badge_url: defaultContent.getBadgeUrl() ?? undefined,
        is_using_domain_default_icon: defaultContent.getIsUsingDomainDefaultIcon(),
        is_using_domain_default_badge: defaultContent.getIsUsingDomainDefaultBadge(),
        auto_suggested_fields_results: defaultContent.getAutoSuggestedFieldsResults(),
        actions: templateActions,
        is_silent: exp.getIsSilent() ?? false,
        require_interaction: exp.getRequireInteraction() ?? false,
        display_meta: {
            ...actionsDisplayMeta,
        },
    })

    // build channels request
    const channelsRequest: {
        default: CreateNotificationTemplateDefaultChannelRequest
        native_ios?: NotificationTemplateNativeIosChannelRequest
        native_android?: NotificationTemplateNativeAndroidChannel
        web?: NotificationTemplateWebChannel
    } = {
        default: defaultChannel,
    }

    // build ios content
    if (channels.includes(DeliveryChannel.NATIVE_IOS)) {
        let badgeBehavior: BadgeBehaviorModel | null = nativeIosContent.getBadgeBehavior() ?? null
        if (!badgeBehavior || badgeBehavior.getAction() === IosBadgeBehaviorAction.DO_NOTHING) {
            badgeBehavior = null
        }

        channelsRequest.native_ios = NotificationTemplateNativeIosChannelRequest.from({
            landing_url: defaultContent.getNativeAltUrl() ?? undefined,
            category: nativeIosContent.getCategory() ?? undefined,
            subtitle: nativeIosContent.getSubtitle() ?? undefined,
            sound: nativeIosContent.getSound() ?? undefined,
            interruption_level: nativeIosContent.getInterruptionLevel() ?? undefined,
            relevance_score: nativeIosContent.getRelevance() ?? undefined,
            target_content_id: nativeIosContent.getTargetContentId() ?? undefined,
            title: nativeIosContent.getTitle() ?? undefined,
            body: nativeIosContent.getBody() ?? undefined,
            icon_url: nativeIosContent.getIconUrl() ?? undefined,
            image_url: nativeIosContent.getImageUrl() ?? undefined,
            badge_behavior: badgeBehavior
                ? CreateBadgeBehaviorRequest.from({
                      action: safeEnumFromValue(ModelsIosBadgeBehaviorAction, badgeBehavior.getAction()),
                      value: badgeBehavior.getValue(),
                  })
                : undefined,
        })
    }

    // build native android content
    if (channels.includes(DeliveryChannel.NATIVE_ANDROID)) {
        channelsRequest.native_android = NotificationTemplateNativeAndroidChannel.from({
            landing_url: defaultContent.getNativeAltUrl() ?? undefined,
            title: nativeAndroidContent.getTitle() ?? undefined,
            body: nativeAndroidContent.getBody() ?? undefined,
            icon_url: nativeAndroidContent.getIconUrl() ?? undefined,
            image_url: nativeAndroidContent.getImageUrl() ?? undefined,
            channel_id: nativeAndroidContent.getChannelId() ?? undefined,
            // actions from UI are built via default -- eventually will decouple
            actions: templateActions,
            display_meta: {
                ...actionsDisplayMeta,
            },
        })
    }

    // build web content
    if (channels.includes(DeliveryChannel.WEB)) {
        channelsRequest.web = NotificationTemplateWebChannel.from({
            // currently no separation between default and current
            landing_url: defaultContent.getLandingUrl() ?? undefined,
            title: webContent.getTitle() || undefined,
            body: webContent.getBody()?.trim() || undefined,
            image_url: webContent.getImageUrl()?.trim() ?? undefined,
            icon_url: webContent.getIconUrl()?.trim() ?? undefined,
            // actions from UI are built via default -- eventually will decouple
            actions: templateActions,
            display_meta: {
                ...actionsDisplayMeta,
            },
        })
    }

    const template = CreateNotificationTemplateRequest.from({
        channels: CreateNotificationTemplateChannelsRequest.from(channelsRequest),
        keywords: defaultContent.getKeywords() ?? [],
        auto_keyword_discovery: hasAutoKwDiscoveryFlag ? true : undefined,
    })

    const collapseKey = normalizeCollapseKey(exp.getCollapseKey()) ?? undefined

    return stripUndefined(
        CreateDomainNotificationRequest.from({
            source: NotificationSource.CAMPAIGN,
            meta: {
                origin_ui: 'new-notif-flow',
            },
            channels,
            collapse_key: collapseKey,
            audience: audiencePayload,
            delivery_spec: deliverySpec,
            template,
        }),
    )
}

/**
 * Projects a rich in-memory CreateDomainNotificationRequest down to the minimal
 * IJourneyActionNotificationRequest wire format for the journey API.
 *
 * Extracts only template, channels, delivery_spec (ttl_seconds + ttl_metric),
 * and collapse_key. Strips source, meta, audience, id, ab_test, topology, and
 * all AbstractStruct class metadata / Symbol properties so the result passes
 * structuredClone() without throwing.
 */
export function toJourneyNotificationPayload(
    notification: CreateDomainNotificationRequest | IJourneyActionNotificationRequest,
    domain: DomainDto,
): IJourneyActionNotificationRequest {
    const deliverySpec = notification.delivery_spec
    const ttl_seconds = deliverySpec?.ttl_seconds ?? domain.defaultNotificationTtlSeconds
    const ttl_metric = deliverySpec?.ttl_metric ?? 'days'

    // JSON round-trip strips class instances, Symbol properties, and prototype
    // methods in one pass. Safe here because notification wire payloads contain
    // only JSON-safe values (strings, numbers, arrays, plain objects).
    return JSON.parse(
        JSON.stringify({
            template: notification.template,
            channels: notification.channels,
            delivery_spec: { ttl_seconds, ttl_metric },
            collapse_key: notification.collapse_key,
        }),
    )
}

// end region
